import { useState, useMemo } from 'react';
import { Email, Account } from '../lib/api';
import { formatDistanceToNow } from '../lib/utils';
import { Calendar, Clock, Mail } from 'lucide-react';

interface DailyBriefProps {
  selectedEmail: Email | null;
  emails: Email[];
  accounts: Account[];
  selectedAccountId: string | null;
  onSelectAccountId: (id: string) => void;
  onSelectEmail: (email: Email) => void;
  isLoading: boolean;
}

type TimeOfDay = 'morning' | 'afternoon';

export default function DailyBrief({
  selectedEmail,
  emails,
  accounts,
  selectedAccountId,
  onSelectAccountId,
  onSelectEmail,
  isLoading,
}: DailyBriefProps) {
  const [timeOfDay, setTimeOfDay] = useState<TimeOfDay>('morning');

  // Get current date formatted nicely
  const currentDate = useMemo(() => {
    const today = new Date();
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    
    return `${days[today.getDay()]}, ${months[today.getMonth()]} ${today.getDate()}, ${today.getFullYear()}`;
  }, []);

  // Filter emails for today
  const todayEmails = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return emails.filter(email => {
      const emailDate = new Date(email.receivedAt);
      emailDate.setHours(0, 0, 0, 0);
      return emailDate.getTime() === today.getTime();
    });
  }, [emails]);

  // Separate newsletters and key emails
  const newsletters = useMemo(() => {
    return todayEmails.filter(e => e.isNewsletter);
  }, [todayEmails]);

  const keyEmails = useMemo(() => {
    return todayEmails
      .filter(e => !e.isNewsletter && (e.actionRequired || e.urgency === 'high'))
      .sort((a, b) => new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime());
  }, [todayEmails]);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        {/* Date */}
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-5 h-5 text-gray-400" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {currentDate}
          </h1>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between">
          {/* Time of Day Toggle */}
          <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
            <button
              onClick={() => setTimeOfDay('morning')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                timeOfDay === 'morning'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              🌅 Morning
            </button>
            <button
              onClick={() => setTimeOfDay('afternoon')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                timeOfDay === 'afternoon'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              ☀️ Afternoon
            </button>
          </div>

          {/* Account Selector */}
          <select
            value={selectedAccountId || ''}
            onChange={(e) => onSelectAccountId(e.target.value)}
            className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-sm border-0 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            <option value="">All Accounts</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.email}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-6 space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="p-6 space-y-8">
            {/* Newsletter Section */}
            {newsletters.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <span className="text-2xl">📰</span>
                  Newsletters
                </h2>
                <div className="space-y-3">
                  {newsletters.map((email) => (
                    <button
                      key={email.id}
                      onClick={() => onSelectEmail(email)}
                      className={`w-full text-left p-4 rounded-lg border transition-all ${
                        selectedEmail?.id === email.id
                          ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h3 className="font-medium text-gray-900 dark:text-white flex-1">
                          {email.subject || '(No subject)'}
                        </h3>
                        {email.summary && (
                          <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full shrink-0">
                            ✨ Summarized
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                        {email.summary || email.bodyPreview || '(No content)'}
                      </p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-gray-500 dark:text-gray-400">
                        <span>{email.from.split('<')[0].trim()}</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDistanceToNow(email.receivedAt)}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Key Emails Section */}
            {keyEmails.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <span className="text-2xl">📌</span>
                  Key Emails
                </h2>
                <div className="space-y-3">
                  {keyEmails.map((email) => (
                    <button
                      key={email.id}
                      onClick={() => onSelectEmail(email)}
                      className={`w-full text-left p-4 rounded-lg border transition-all ${
                        selectedEmail?.id === email.id
                          ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h3 className="font-medium text-gray-900 dark:text-white flex-1 line-clamp-1">
                          {email.subject || '(No subject)'}
                        </h3>
                        <div className="flex items-center gap-2 shrink-0">
                          {!email.isRead && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full" title="Unread" />
                          )}
                          {email.actionRequired && (
                            <span className="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full">
                              Action
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                        {email.bodyPreview || '(No preview)'}
                      </p>
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span className="truncate">{email.from.split('<')[0].trim()}</span>
                        <span className="flex items-center gap-1 shrink-0">
                          <Clock className="w-3 h-3" />
                          {formatDistanceToNow(email.receivedAt)}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Empty State */}
            {newsletters.length === 0 && keyEmails.length === 0 && todayEmails.length === 0 && (
              <div className="flex items-center justify-center py-12 text-center">
                <div>
                  <Mail className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">
                    No emails for today
                  </p>
                </div>
              </div>
            )}

            {/* Show all other emails if none of above */}
            {newsletters.length === 0 && keyEmails.length === 0 && todayEmails.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Other Emails
                </h2>
                <div className="space-y-3">
                  {todayEmails.map((email) => (
                    <button
                      key={email.id}
                      onClick={() => onSelectEmail(email)}
                      className={`w-full text-left p-4 rounded-lg border transition-all ${
                        selectedEmail?.id === email.id
                          ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h3 className="font-medium text-gray-900 dark:text-white flex-1 line-clamp-1">
                          {email.subject || '(No subject)'}
                        </h3>
                        {!email.isRead && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                        {email.bodyPreview || '(No preview)'}
                      </p>
                    </button>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
